import { Button, Checkbox, Input, Select, Table, TableColumnsType } from "antd";
import { useEffect, useState } from "react";
import "../../bdoStyling/bdostyling.css";
import useFilters from "../../utils/hooks/useFilter/useFilters";
import useSorter from "../../utils/hooks/useSorter/useSorter";
import HeaderCellRenderer from "../header-cell-renderer/HeaderCellRenderer";
import TextHighlighter from "../../utils/text-highlighter/TextHighlighter";
import { TablePageProps } from "./TablePage.types";
import "./TablePage.css";
import { MdClose } from "react-icons/md";

const moduleState = {
  selectedPageSizes: {} as Record<string, string>,
  selectedPageNumbers: {} as Record<string, string>,
};

function TablePage({
  pageConfiguration,
  refreshDataToggle,
  setParentDataView,
  setParentDataAll,
}: TablePageProps) {
  const {
    setData: setSourceData,
    setFilterValue,
    getFilterValue,
    filteredData,
  } = useFilters({ pageName: pageConfiguration.pageName });

  const {
    getClassOfColumnSortOrder,
    stepSortOrderForColumn,
    setData: setFilteredData,
    sortedData,
  } = useSorter({ pageName: pageConfiguration.pageName });

  useEffect(() => {
    setFilteredData(filteredData);
    if (window && window.document) {
      const elementsWithTitleAttribute = window.document.querySelectorAll(
        "span.ant-select-selection-item[title]"
      );
      elementsWithTitleAttribute.forEach((elementWithTitleAttribute) => {
        elementWithTitleAttribute.removeAttribute("title");
      });
    }
    // eslint-disable-next-line
  }, [filteredData]);

  useEffect(() => {
    setParentDataView(sortedData); //send filteredData to parent, who will use it for Excel export
    // eslint-disable-next-line
  }, [sortedData]);

  const [isLoading, setIsLoading] = useState(false);
  const [refreshDataTable, setRefreshDataTable] = useState(true);

  const getTableData = () => {
    setIsLoading(true);
    pageConfiguration
      .dataApi()
      .then((response) => {
        if (response.error) {
          console.error("<TablePage/> dataAPi error:", response.error);
        } else {
          // console.log("<TablePage/> setSourceData:", response.data);
          setSourceData(response.data);
          setParentDataAll && setParentDataAll(response.data);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (!setSourceData || !pageConfiguration) {
      console.log(
        "UseEffect[refreshDataToggle, setSourceData] DENIED:",
        setSourceData,
        pageConfiguration.dataApi
      );
      return;
    }

    // console.log("UseEffect[refreshDataToggle, setSourceData] Refreshing Data!");
    getTableData();
    // eslint-disable-next-line
  }, [refreshDataToggle]);

  const columns: TableColumnsType<any> =
    pageConfiguration.columnDefinitions.map(
      ({
        titleText,
        dataIndex,
        ignoreChars,
        render: renderWrapper,
        showFilter = true,
        filterOptionValues: optionValues,
        sortAsNumber = false,
        width,
      }) => ({
        key: dataIndex,
        ...(width ? { width: width } : {}),
        title: HeaderCellRenderer({
          dataIndex,
          titleText,
          inputFilter:
            showFilter &&
            (optionValues ? (
              <Select
                dropdownRender={(menu) => (
                  <div className="flex flex-col">
                    <div className="">{menu}</div>
                    <div className="flex flex-col items-end text-md">
                      <Button
                        type="text"
                        onClick={(e) => {
                          const currentPageNumber =
                            moduleState.selectedPageNumbers[
                              pageConfiguration.pageName
                            ];
                          if (currentPageNumber !== "1") {
                            moduleState.selectedPageNumbers[
                              pageConfiguration.pageName
                            ] = "1";
                          }

                          setFilterValue(
                            dataIndex,
                            [],
                            ignoreChars,
                            true /*exactMatch*/
                          );
                        }}
                      >
                        <span className="flex flex-row  mr-1 pb-2">
                          <p className="text-xl mr-1">
                            <MdClose />
                          </p>
                          <p> Clear</p>
                        </span>
                      </Button>
                    </div>
                  </div>
                )}
                placeholder={`Search ${titleText}`}
                className="w-full font-normal"
                value={getFilterValue(dataIndex) || null}
                mode="multiple"
                optionLabelProp="children"
                options={[
                  ...optionValues.map((optionValue) => {
                    return {
                      title: "",
                      value: optionValue,
                      label: (
                        <Checkbox
                          checked={(
                            (getFilterValue(dataIndex) as string[]) || []
                          ).includes(optionValue)}
                        >
                          {" "}
                          {optionValue === " " ? (
                            <>{`No ${titleText}`}</>
                          ) : (
                            optionValue
                          )}
                        </Checkbox>
                      ),
                    };
                  }),
                ]}
                onChange={(value: any, option: any) => {
                  const currentPageNumber =
                    moduleState.selectedPageNumbers[pageConfiguration.pageName];
                  if (currentPageNumber !== "1") {
                    moduleState.selectedPageNumbers[
                      pageConfiguration.pageName
                    ] = "1";
                  }
                  setFilterValue(
                    dataIndex,
                    value,
                    ignoreChars,
                    true /*exactMatch*/
                  );
                }}
                tagRender={(e) => {
                  if (!getFilterValue(dataIndex)) {
                    return <></>;
                  }
                  const selectedFilters = (getFilterValue(dataIndex) ||
                    []) as string[];
                  const itemCount = selectedFilters.length;
                  return (
                    <span>{`${itemCount} item${itemCount !== 1 ? "s" : ""} selected`}</span>
                  );
                }}
                maxTagCount={0}
              ></Select>
            ) : (
              <Input
                className="font-normal"
                placeholder={`Search ${titleText}`}
                defaultValue={getFilterValue(dataIndex)}
                type="text"
                onChange={(e) => {
                  setFilterValue(
                    dataIndex,
                    e.target.value,
                    ignoreChars,
                    sortAsNumber /*exactMatch*/
                  );
                }}
              />
            )),
          getSortOrder: () => getClassOfColumnSortOrder(dataIndex),
          onClick: () =>
            stepSortOrderForColumn({
              columnName: dataIndex,
              cumulativeSort: false,
              sortAsNumber,
            }),
        }),

        // LFC: We don't pass in dataIndex to the antd column definition
        // If we define dataIndex, then antd calls render() with the value of that dataIndex
        // and not the whole record.
        // We want antd to ALWAYS call render() with the whole record so that we can deal with nested
        // field names e.g. "pool.name", which is not possible if we define dataIndex.

        // dataIndex,

        render: (obj: any) => {
          const cellText = dataIndex
            .split(".")
            .reduce((acc, part) => acc && acc[part], obj);
          const searchText = getFilterValue(dataIndex);

          if (renderWrapper) {
            //custom renderer: pass everything up to caller.
            return renderWrapper({
              dataRow: obj,
              cellText,
              searchText,
              ignoreChars,
            });
          }

          //default renderer: highlighted text based on user filter
          return (
            <TextHighlighter
              bodyText={cellText}
              searchText={searchText}
              ignoreChars={ignoreChars}
            />
          );
        },
      })
    );

  return (
    <div className="flex flex-col w-full">
      <div className="flex-row px-8 w-full">
        <Table
          pagination={{
            ...(moduleState.selectedPageSizes[pageConfiguration.pageName]
              ? {
                  pageSize: parseInt(
                    moduleState.selectedPageSizes[pageConfiguration.pageName]
                  ),
                }
              : { pageSize: 25 }),
            ...(moduleState.selectedPageNumbers[pageConfiguration.pageName]
              ? {
                  current: parseInt(
                    moduleState.selectedPageNumbers[pageConfiguration.pageName]
                  ),
                }
              : {}),
            showTitle: false,
            showSizeChanger: true,
            pageSizeOptions: ["10", "25", "50", "100", "250"],
            onChange: (page, pageSize) => {
              moduleState.selectedPageSizes[pageConfiguration.pageName] =
                pageSize.toString();
              moduleState.selectedPageNumbers[pageConfiguration.pageName] =
                page.toString();
              setRefreshDataTable(!refreshDataTable);
            },
          }}
          dataSource={sortedData}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 1500, y: "calc(100vh - 350px)" }}
        />
      </div>
    </div>
  );
}

export default TablePage;
